'use client'
import { TitleDivider } from '@/components/TitleDivider'
import { Typography } from 'components/Contentful'
import Section, { SECTION_VARIANTS } from 'components/Section'
import { useMemoizedContentGetter } from 'utils/contentful'
import classNames from 'classnames'
import { NewCarousel } from '@/components/Carousels/NewCarousel'
import { Image } from 'components/Image'
import { Slider } from '@achieve/ascend'
import { formatDollar } from './utils'
import { useDebtAssessment } from './useDebtAssessment'
import { MinimumPaymentsCard } from './MinimumPaymentsCard'
import { OptionCardContent } from './OptionCardContent'

function DebtPayoffCalculatorModule({ config, title, eyebrow, subTitle, disclaimer }) {
  const sliderConfig = config?.jsonContent?.slider || {}
  const apiUrl = config?.jsonContent?.apiUrl || '/api/achieve-tools/debt-assessment'
  const backgroundColor = config?.jsonContent?.backgroundColor || ''

  const { selectedValue, isLoading, results, handleChange, handleChangeCommitted } =
    useDebtAssessment(sliderConfig, apiUrl, config?.jsonContent?.defaultValues)

  return (
    <div className="relative">
      <div className="absolute inset-y-0 left-[calc((50%-50vw)-1%)] w-screen bg-[#F8F9FC] -z-10"></div>
      <Section
        contain={false}
        variant={config?.jsonContent?.sectionVariant ?? SECTION_VARIANTS.white}
        backgroundColor={backgroundColor}
        className={'py-12 px-[10px] overflow-hidden lg:py-12 lg:px-8'}
        data-testid="DebtPayoffCalculator_section"
      >
        <div className={'w-full'}>
          <TitleDivider
            eyebrowBlack={true}
            eyebrow={eyebrow}
            title={title}
            subtitle={subTitle}
            className="mb-4 lg:mb-7"
            classNameTitleText="lg:text-[32px] text-[24px] font-bold"
            classNameSubtitleText="text-[#42546B] text-[14px] lg:text-[18px]"
          />
          <div className="max-w-[614px] mx-auto mt-0 mb-3 pr-0 lg:mt-2 lg:mb-6 lg:pr-8">
            <Typography
              content={`$${formatDollar(selectedValue)}${
                selectedValue === sliderConfig.max && sliderConfig.maxPlus ? '+' : ''
              }`}
              variant="displayMd"
              fontWeight="bold"
              className={'text-[#42546B] text-center font-medium'}
              data-testid="product-hero-dollar-value"
            />
            <Slider
              aria-label="dollar-amount-selection-slider"
              className={classNames(
                'w-full',
                // Child MuiSlider-thumb
                '[&_.MuiSlider-thumb]:bg-[#ffffff]',
                '[&_.MuiSlider-thumb]:border-[#E7ECF3]',
                '[&_.MuiSlider-thumb]:border-2',
                '[&_.MuiSlider-thumb]:border-solid',
                '[&_.MuiSlider-thumb]:h-5'
              )}
              classes={{
                mark: 'hidden',
              }}
              disableSwap={true}
              disabled={isLoading}
              data-testid="product-hero-slider-input"
              value={selectedValue}
              step={sliderConfig.step || 1000}
              min={sliderConfig.min}
              max={sliderConfig.max}
              marks={[
                {
                  value: sliderConfig.min,
                  label: (
                    <Typography
                      content={`$${formatDollar(sliderConfig.min)}`}
                      variant="bodySm"
                      className="ml-[50px] font-normal text-xs pt-1 text-[#5C708A]"
                      data-testid="product-hero-slider-min"
                    />
                  ),
                },
                {
                  value: sliderConfig.max,
                  label: (
                    <Typography
                      content={`$${formatDollar(sliderConfig.max)}${
                        sliderConfig.maxPlus ? '+' : ''
                      }`}
                      variant="bodySm"
                      className="ml-[-60px] font-normal text-xs pt-1 text-[#5C708A]"
                      data-testid="product-hero-slider-max"
                    />
                  ),
                },
              ]}
              onChange={handleChange}
              onChangeCommitted={handleChangeCommitted}
            />
          </div>
          {/* Mobile view */}
          <div className="block lg:hidden">
            <NewCarousel
              showButtons={false}
              indicatorsContainerClass="bottom-[-20px] lg:bottom-[-32px]"
              autoPlay={config?.jsonContent?.autoPlay || false}
              idPrefix={'TABS_ID_PREFIX'}
              dotExtraPadding={true}
              peakSlider={true}
              trackClassName="gap-2 sm:gap-5"
              items={[
                <div key={`slide-${1}`} className="h-full flex items-center justify-center">
                  <div className="grid">
                    <Typography
                      className="lg:col-span-2 bg-[linear-gradient(164.58deg,#2C47F6_-0.47%,#3B6AF7_54.85%)] border border-solid border-[rgba(148,187,254,1)] text-white text-center pb-7 pt-2 font-medium text-[16px] rounded-t-xl mx-[12px] lg:mx-[24px] mb-[-28px] "
                      content={config?.jsonContent?.header1 || 'Loan options'}
                    />
                    <div className="flex gap-2 min-w-0">
                      <div
                        className={`flex-1 flex-col rounded-b-xl overflow-hidden mt-2 rounded-[10px] border border-solid min-h-[450px] sm:min-h-[550px] lg:min-h-[500px] ${
                          results?.[0]?.active
                            ? 'bg-white overflow-hidden  border-[#C0CBD8]'
                            : 'bg-[#F8F9FC] border-[#F8F9FC]'
                        }`}
                      >
                        <OptionCardContent
                          icon={
                            <Image
                              alt={'Personal Loans'}
                              height="60"
                              src="/APL.svg"
                              width="60"
                              className="lg:mt-4 my-2 lg:mb-6"
                            />
                          }
                          title={config?.jsonContent?.title1 || 'Personal Loans¹'}
                          badgeText={config?.jsonContent?.badge1 || 'Credit score 640+'}
                          active={results?.[0]?.active}
                          unavailableText={results?.[0]?.unavailableText}
                          resultMonthly={results?.[0]?.resultMonthly}
                          resultMonthlyText={
                            results?.[0]?.resultMonthlyText || 'Est. monthly payment⁵'
                          }
                          resultMonths={results?.[0]?.resultMonths}
                          resultMonthsText={
                            results?.[0]?.resultTotalText || 'Est. time to pay off debt⁷'
                          }
                          resultTotal={results?.[0]?.resultTotal}
                          resultTotalText={
                            results?.[0]?.resultMonthsText || 'Est. total debt to pay off⁶'
                          }
                          disclaimer={
                            config?.jsonContent?.disclaimer1 || 'No credit impact to check plan'
                          }
                          pos={0}
                          cta1={{
                            text: results?.[0]?.ctaText1,
                            link: results?.[0]?.ctaLink1,
                            track: results?.[0]?.track1,
                          }}
                          cta2={{
                            text: results?.[0]?.ctaText2,
                            link: results?.[0]?.ctaLink2,
                            track: results?.[0]?.track2,
                          }}
                        />
                      </div>
                      <div
                        className={`flex-1 flex-col rounded-b-xl overflow-hidden mt-2 rounded-[10px] border border-solid ${
                          results?.[1]?.active
                            ? 'bg-white overflow-hidden  border-[#C0CBD8]'
                            : 'bg-[#F8F9FC] border-[#F8F9FC]'
                        }`}
                      >
                        <OptionCardContent
                          icon={
                            <Image
                              alt={'Home Equity Loans'}
                              height="60"
                              src="/HELOC.svg"
                              width="60"
                              className="lg:mt-4 my-2 lg:mb-6"
                            />
                          }
                          title={config?.jsonContent?.title2 || 'Home Equity Loans²'}
                          badgeText={config?.jsonContent?.badge2 || 'Credit score 600+'}
                          active={results?.[1]?.active}
                          unavailableText={results?.[1]?.unavailableText}
                          resultMonthly={results?.[1]?.resultMonthly}
                          resultMonthlyText={
                            results?.[1]?.resultMonthlyText || 'Est. monthly payment⁵'
                          }
                          resultMonths={results?.[1]?.resultMonths}
                          resultMonthsText={
                            results?.[1]?.resultTotalText || 'Est. time to pay off debt⁷'
                          }
                          resultTotal={results?.[1]?.resultTotal}
                          resultTotalText={
                            results?.[1]?.resultMonthsText || 'Est. total debt to pay off⁶'
                          }
                          disclaimer={
                            config?.jsonContent?.disclaimer2 || 'No credit impact to check plan'
                          }
                          pos={1}
                          cta1={{
                            text: results?.[1]?.ctaText1,
                            link: results?.[1]?.ctaLink1,
                            track: results?.[1]?.track1,
                          }}
                          cta2={{
                            text: results?.[1]?.ctaText2,
                            link: results?.[1]?.ctaLink2,
                            track: results?.[1]?.track2,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>,
                <div
                  key={`slide-${1}`}
                  className="h-full flex items-center justify-center mt-[6px] sm:mt-0"
                >
                  <div className="grid">
                    <div className="flex mb-[-14px]">
                      <div className="flex-1 ml-[-4px] lg:ml-[-10px]">
                        <Typography
                          className="text-nowrap px-1 sm:px-2 bg-[linear-gradient(182.35deg,#00B67A_-24.44%,#5C8FEB_154.67%)] text-white text-center py-2 font-medium text-[16px] rounded-t-xl border border-solid border-[rgba(236,249,244,1)] mx-[10px] sm:mx-[12px] lg:mx-[24px] border-b-0"
                          content={config?.jsonContent?.header2 || 'Non-loan option'}
                        />
                      </div>
                      <div className="flex-1"></div>
                    </div>
                    <div className="flex gap-2 min-w-0">
                      <div
                        className={`flex-1 flex-col rounded-b-xl   overflow-hidden mt-2 rounded-[10px] border border-solid min-h-[450px] sm:min-h-[550px] lg:min-h-[500px] ${
                          results?.[2]?.active
                            ? 'bg-white overflow-hidden  border-[#C0CBD8]'
                            : 'bg-[#F8F9FC] border-[#F8F9FC]'
                        }`}
                      >
                        <OptionCardContent
                          icon={
                            <Image
                              alt={'Debt Relief'}
                              height="60"
                              src="/ADR.svg"
                              width="60"
                              className="lg:mt-4 my-2 lg:mb-6"
                            />
                          }
                          title={config?.jsonContent?.title3 || 'Debt Relief³'}
                          badgeText={config?.jsonContent?.badge3 || 'No credit minimum'}
                          active={results?.[2]?.active}
                          unavailableText={results?.[2]?.unavailableText}
                          resultMonthly={results?.[2]?.resultMonthly}
                          resultMonthlyText={
                            results?.[2]?.resultMonthlyText || 'Est. monthly payment⁵'
                          }
                          resultMonths={results?.[2]?.resultMonths}
                          resultMonthsText={
                            results?.[2]?.resultTotalText || 'Est. time to pay off debt⁷'
                          }
                          resultTotal={results?.[2]?.resultTotal}
                          resultTotalText={
                            results?.[2]?.resultMonthsText || 'Est. total debt to pay off⁶'
                          }
                          disclaimer={
                            config?.jsonContent?.disclaimer3 || 'No credit impact to check plan'
                          }
                          pos={0}
                          cta1={{
                            text: results?.[2]?.ctaText1,
                            link: results?.[2]?.ctaLink1,
                            track: results?.[2]?.track1,
                          }}
                          cta2={{
                            text: results?.[2]?.ctaText2,
                            link: results?.[2]?.ctaLink2,
                            track: results?.[2]?.track2,
                          }}
                        />
                      </div>
                      <MinimumPaymentsCard
                        config={config}
                        result={results?.[3]}
                        className="flex-1 flex-col rounded-b-xl bg-white overflow-hidden mt-2 rounded-[10px] border border-solid border-[#C0CBD8]"
                      />
                    </div>
                  </div>
                </div>,
              ]}
              interval={5000}
            />
          </div>
          {/* Desktop view */}
          <div className="hidden lg:block p-4 sm:p-8 font-ultramarine">
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto"
              data-testid="DebtPayoffCalculator-container"
            >
              <Typography
                className="lg:col-span-2 bg-[linear-gradient(164.58deg,#2C47F6_-0.47%,#3B6AF7_54.85%)] border border-solid border-[rgba(148,187,254,1)] text-white text-center py-2 font-medium text-[16px] rounded-t-xl mx-[12px] lg:mx-[24px] mb-[-28px] "
                content={config?.jsonContent?.header1 || 'Loan options'}
                data-testid="DebtPayoffCalculator-loan-options-header"
              />
              <Typography
                className="text-nowrap bg-[linear-gradient(182.35deg,#00B67A_-24.44%,#5C8FEB_154.67%)] text-white text-center py-2 font-medium text-[16px] rounded-t-xl border border-solid border-[rgba(236,249,244,1)] mx-[12px] lg:mx-[24px] border-b-0"
                content={config?.jsonContent?.header2 || 'Non-loan option'}
                data-testid="DebtPayoffCalculator-non-loan-option-header"
              />
              <MinimumPaymentsCard
                config={config}
                result={results?.[3]}
                className="lg:row-span-2 flex flex-col bg-white overflow-hidden rounded-[10px] border border-solid border-[#C0CBD8] mt-[38px]"
              />
              <div
                className={`flex flex-col rounded-b-xl -mt-1 lg:-mt-6 rounded-[10px] border border-solid min-h-[450px] sm:min-h-[550px] lg:min-h-[500px] ${
                  results?.[0]?.active
                    ? 'bg-white overflow-hidden  border-[#C0CBD8]'
                    : 'bg-[#F8F9FC] border-[#F8F9FC]'
                }`}
              >
                <OptionCardContent
                  icon={
                    <Image
                      alt={'Personal Loans'}
                      height="60"
                      src="/APL.svg"
                      width="60"
                      className="lg:mt-4 my-2 lg:mb-6"
                    />
                  }
                  title={config?.jsonContent?.title1 || 'Personal Loans¹'}
                  badgeText={config?.jsonContent?.badge1 || 'Credit score 640+'}
                  active={results?.[0]?.active}
                  unavailableText={results?.[0]?.unavailableText}
                  resultMonthly={results?.[0]?.resultMonthly}
                  resultMonthlyText={results?.[0]?.resultMonthlyText || 'Est. monthly payment⁵'}
                  resultMonths={results?.[0]?.resultMonths}
                  resultMonthsText={results?.[0]?.resultTotalText || 'Est. time to pay off debt⁷'}
                  resultTotal={results?.[0]?.resultTotal}
                  resultTotalText={results?.[0]?.resultMonthsText || 'Est. total debt to pay off⁶'}
                  disclaimer={config?.jsonContent?.disclaimer1 || 'No credit impact to check plan'}
                  pos={0}
                  cta1={{
                    text: results?.[0]?.ctaText1,
                    link: results?.[0]?.ctaLink1,
                    track: results?.[0]?.track1,
                  }}
                  cta2={{
                    text: results?.[0]?.ctaText2,
                    link: results?.[0]?.ctaLink2,
                    track: results?.[0]?.track2,
                  }}
                />
              </div>
              <div
                className={`
            flex flex-col rounded-b-xl overflow-hidden -mt-1 lg:-mt-6 rounded-[10px] border border-solid
            ${
              results?.[1]?.active
                ? 'bg-white overflow-hidden  border-[#C0CBD8]'
                : 'bg-[#F8F9FC] border-[#F8F9FC]'
            }`}
              >
                <OptionCardContent
                  icon={
                    <Image
                      alt={'Home Equity Loans'}
                      height="60"
                      src="/HELOC.svg"
                      width="60"
                      className="lg:mt-4 my-2 lg:mb-6"
                    />
                  }
                  title={config?.jsonContent?.title2 || 'Home Equity Loans²'}
                  badgeText={config?.jsonContent?.badge2 || 'Credit score 600+'}
                  active={results?.[1]?.active}
                  unavailableText={results?.[1]?.unavailableText}
                  resultMonthly={results?.[1]?.resultMonthly}
                  resultMonthlyText={results?.[1]?.resultMonthlyText || 'Est. monthly payment⁵'}
                  resultMonths={results?.[1]?.resultMonths}
                  resultMonthsText={results?.[1]?.resultTotalText || 'Est. time to pay off debt⁷'}
                  resultTotal={results?.[1]?.resultTotal}
                  resultTotalText={results?.[1]?.resultMonthsText || 'Est. total debt to pay off⁶'}
                  disclaimer={config?.jsonContent?.disclaimer2 || 'No credit impact to check plan'}
                  pos={1}
                  cta1={{
                    text: results?.[1]?.ctaText1,
                    link: results?.[1]?.ctaLink1,
                    track: results?.[1]?.track1,
                  }}
                  cta2={{
                    text: results?.[1]?.ctaText2,
                    link: results?.[1]?.ctaLink2,
                    track: results?.[1]?.track2,
                  }}
                />
              </div>
              <div
                className={`
              flex flex-col rounded-b-xl  overflow-hidden -mt-1 lg:-mt-6 rounded-[10px] border border-solid
              ${
                results?.[2]?.active
                  ? 'bg-white overflow-hidden  border-[#C0CBD8]'
                  : 'bg-[#F8F9FC] border-[#F8F9FC]'
              }`}
              >
                <OptionCardContent
                  icon={
                    <Image
                      alt={'Debt Relief'}
                      height="60"
                      src="/ADR.svg"
                      width="60"
                      className="lg:mt-4 my-2 lg:mb-6"
                    />
                  }
                  title={config?.jsonContent?.title3 || 'Debt Relief³'}
                  badgeText={config?.jsonContent?.badge3 || 'No credit minimum'}
                  active={results?.[2]?.active}
                  unavailableText={results?.[2]?.unavailableText}
                  resultMonthly={results?.[2]?.resultMonthly}
                  resultMonthlyText={results?.[2]?.resultMonthlyText || 'Est. monthly payment⁵'}
                  resultMonths={results?.[2]?.resultMonths}
                  resultMonthsText={results?.[2]?.resultTotalText || 'Est. time to pay off debt⁷'}
                  resultTotal={results?.[2]?.resultTotal}
                  resultTotalText={results?.[2]?.resultMonthsText || 'Est. total debt to pay off⁶'}
                  disclaimer={config?.jsonContent?.disclaimer3 || 'No credit impact to check plan'}
                  pos={0}
                  cta1={{
                    text: results?.[2]?.ctaText1,
                    link: results?.[2]?.ctaLink1,
                    track: results?.[2]?.track1,
                  }}
                  cta2={{
                    text: results?.[2]?.ctaText2,
                    link: results?.[2]?.ctaLink2,
                    track: results?.[2]?.track2,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {disclaimer && (
          <Typography
            data-testid="title-divider-disclaimer"
            className="max-w-[808px] text-center text-[#42546b] text-[12px] m-auto pt-8 lg:pt-4"
            content={disclaimer.textContent}
          />
        )}
      </Section>
    </div>
  )
}

function DebtPayoffCalculator({ content }) {
  let contentToGet = ['config', 'title', 'eyebrow', 'subTitle', 'disclaimer']

  const { config, title, eyebrow, subTitle, disclaimer } = useMemoizedContentGetter(
    content,
    contentToGet
  )

  return (
    <DebtPayoffCalculatorModule
      config={config}
      title={title}
      eyebrow={eyebrow}
      subTitle={subTitle}
      disclaimer={disclaimer}
    />
  )
}

export { DebtPayoffCalculator, DebtPayoffCalculatorModule }
export default DebtPayoffCalculator
